import React, { useState } from 'react';
import { Box, IconButton, Stack } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import TicketCard from './EventTicket'; // Import the TicketCard component
import FAQItem from '../Collapsible/Collapsible';
import { WorkExperienceCard } from '../Cards/WorkExperienceCard';
import { WorkLinkCard } from '../Cards/WorkLinkCard';
import { SocialMediaCard } from '../Cards/SocialMediaCard';
import { FormGenerator } from '../FormGenerator/FormGenerator';
import { IRegistrationFormField } from '../../types/global/forms.types';
import { useSelector } from 'react-redux';
import { createSystemFormSelector } from '../../selectors/selectors';
import FormCreatorModal from '../FormCreator';
import FormCard from '../Cards/FormCard';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';

export interface IActionRenderer {
    actionType: string;
    actionValues: any[];
    onAddClicked?: () => void;
    onDuplicate?: (actionValue: any, form?: string) => void;
    onEdit?: (actionValue: any, index: number, form?: string) => void;
    onDelete?: (actionValue: any, index: number, form?: string) => void;
    /** Determines if the in-component modal will be used */
    inlineMode?: boolean;
    onSubmit?: (formData: any, parentKey: string, isMultiple?: boolean, isEdit?: boolean, editIndex?: number) => void;
    initialValue?: any;
    initialEditValue?: any;
    formField?: IRegistrationFormField;
    /** The full data from parent */
    initialFormData?: Record<string, any>;
}

/** Handle Edit And Functions in here */

const ActionRenderer: React.FC<IActionRenderer> = ({
    actionType,
    actionValues,
    onAddClicked,
    onDelete,
    onDuplicate,
    onEdit,
    inlineMode,
    onSubmit,
    initialEditValue,
    initialValue,
    formField,
    initialFormData,
    ...props
}) => {
    // Track open state for each actionType separately
    const [openForms, setOpenForms] = useState<{ [key: string]: boolean }>({});
    const [isEdit, setIsEdit] = useState(false);
    const [editValue, setEditValue] = useState<any>(initialEditValue);
    const [editIndex, setEditIndex] = useState<any>();
    const systemFormsByTypeSelector = createSystemFormSelector(
        form => form.identifier === formField?.action_button_props?.form,
    );
    const specificSystemForms = useSelector(systemFormsByTypeSelector);
    const { themeStyle } = useThemeContext();

    /** Get the system fom to display */

    const handleAddCard = (type: string) => {
        setOpenForms(prev => ({ ...prev, [type]: true })); // Open only the selected form
    };

    const handleCloseForm = (type: string) => {
        setOpenForms(prev => ({ ...prev, [type]: false })); // Close only the selected form
        setIsEdit(false);
        setEditValue(undefined);
        setEditIndex(undefined);
    };

    const handleOnSubmit = (payload: any) => {
        onSubmit &&
            onSubmit(payload, specificSystemForms?.parent_key!, specificSystemForms?.multiple, isEdit, editIndex);
    };

    if (['create_new_skill', 'create_other_skill'].includes(actionType)) {
        return (
            <Stack direction="column">
                {!!actionValues &&
                    Array.isArray(actionValues) &&
                    actionValues.map((card, index) => (
                        <WorkExperienceCard
                            experience={card}
                            key={index}
                            onDelete={() => onDelete && onDelete(card, index, actionType)}
                            onDuplicate={() => onDuplicate && onDuplicate(card, actionType)}
                            onEdit={() => {
                                setEditIndex(index);
                                setEditValue(card);
                                setIsEdit(true);
                                handleAddCard(actionType);
                            }}
                            {...props}
                        />
                    ))}
                {/* Add New Card Button */}
                <IconButton
                    onClick={() => handleAddCard(actionType)}
                    sx={{
                        width: '100%',
                        height: 100,
                        backgroundColor: theme[themeStyle].deep_grey,
                        borderRadius: '10px',
                        '&:hover': { backgroundColor: theme[themeStyle].deep_grey },
                    }}>
                    <AddIcon
                        fontSize="large"
                        sx={{
                            color: theme[themeStyle].highlight,
                        }}
                    />
                </IconButton>

                {/* Form Generator */}
                <FormGenerator
                    formFields={specificSystemForms?.form!}
                    open={!!openForms[actionType]} // Ensure only the clicked form is open
                    onClose={() => handleCloseForm(actionType)}
                    onSubmit={handleOnSubmit}
                    initialValue={initialValue}
                    header={specificSystemForms?.title}
                    initialEditValue={editValue}
                    isEdit={isEdit}
                    initialFormData={initialFormData}
                />
            </Stack>
        );
    }

    if (actionType === 'create_new_link') {
        return (
            <Stack direction="column">
                {!!actionValues &&
                    Array.isArray(actionValues) &&
                    actionValues.map((card, index) => (
                        <WorkLinkCard
                            key={index}
                            data={card}
                            onDelete={() => onDelete && onDelete(card, index, actionType)}
                            onDuplicate={() => onDuplicate && onDuplicate(card, actionType)}
                            onEdit={() => {
                                setEditIndex(index);
                                setEditValue(card);
                                setIsEdit(true);
                                handleAddCard(actionType);
                            }}
                            {...props}
                        />
                    ))}
                {/* Add New Card Button */}
                <IconButton
                    onClick={() => handleAddCard(actionType)}
                    sx={{
                        width: '100%',
                        height: 100,
                        backgroundColor: theme[themeStyle].deep_grey,
                        borderRadius: '10px',
                        '&:hover': { backgroundColor: theme[themeStyle].deep_grey },
                    }}>
                    <AddIcon
                        fontSize="large"
                        sx={{
                            color: theme[themeStyle].highlight,
                        }}
                    />
                </IconButton>

                {/* Form Generator */}
                <FormGenerator
                    formFields={specificSystemForms?.form!}
                    open={!!openForms[actionType]} // Ensure only the clicked form is open
                    onClose={() => handleCloseForm(actionType)}
                    onSubmit={handleOnSubmit}
                    initialValue={initialValue}
                    header={specificSystemForms?.title}
                    initialEditValue={editValue}
                    isEdit={isEdit}
                    initialFormData={initialFormData}
                />
            </Stack>
        );
    }

    if (actionType === 'create_event_faq') {
        return (
            <Stack direction="column">
                {!!actionValues &&
                    Array.isArray(actionValues) &&
                    actionValues.map((card, index) => (
                        <FAQItem
                            key={index}
                            question={card.question}
                            answer={card.answer}
                            edit
                            onDelete={() => onDelete && onDelete(card, index, actionType)}
                            onDuplicate={() => onDuplicate && onDuplicate(card, actionType)}
                            onEdit={() => onEdit && onEdit(card, index, actionType)}
                            {...props}
                        />
                    ))}
                {/* Add New Card Button */}
                <IconButton
                    onClick={() => handleAddCard(actionType)}
                    sx={{
                        width: '100%',
                        height: 100,
                        backgroundColor: theme[themeStyle].deep_grey,
                        borderRadius: '10px',
                        '&:hover': { backgroundColor: theme[themeStyle].deep_grey },
                    }}>
                    <AddIcon
                        fontSize="large"
                        sx={{
                            color: theme[themeStyle].highlight,
                        }}
                    />
                </IconButton>

                {/* Form Generator */}
                <FormGenerator
                    formFields={specificSystemForms?.form!}
                    open={!!openForms[actionType]} // Ensure only the clicked form is open
                    onClose={() => handleCloseForm(actionType)}
                    onSubmit={handleOnSubmit}
                    initialValue={initialValue}
                    header={specificSystemForms?.title}
                    initialEditValue={editValue}
                    isEdit={isEdit}
                    initialFormData={initialFormData}
                />
            </Stack>
        );
    }

    if (actionType === 'create_new_form') {
        return (
            <Stack direction={'column'}>
                {!!actionValues &&
                    Array.isArray(actionValues) &&
                    actionValues.map((card, index) => (
                        <FormCard
                            title={''}
                            onDelete={() => onDelete && onDelete(card, index, actionType)}
                            onDuplicate={() => onDuplicate && onDuplicate(card, actionType)}
                            onEdit={() => onEdit && onEdit(card, index, actionType)}
                        />
                    ))}
                {/* Add New Card Button */}
                <IconButton
                    onClick={() => handleAddCard(actionType)}
                    sx={{
                        width: '100%',
                        height: 100,
                        backgroundColor: theme[themeStyle].deep_grey,
                        borderRadius: '10px',
                        '&:hover': {
                            backgroundColor: theme[themeStyle].deep_grey_lighter,
                        },
                    }}>
                    <AddIcon
                        fontSize="large"
                        sx={{
                            color: theme[themeStyle].highlight,
                        }}
                    />
                </IconButton>
                <FormCreatorModal
                    open={openForms[actionType]}
                    onClose={() => handleCloseForm(actionType)}
                    onSave={handleOnSubmit}
                    initialFields={editValue}
                />
            </Stack>
        );
    }

    if (actionType === 'create_new_social') {
        return (
            <Stack direction="column">
                {!!actionValues &&
                    Array.isArray(actionValues) &&
                    actionValues.map((card, index) => (
                        <SocialMediaCard
                            key={index}
                            data={card}
                            onDelete={() => onDelete && onDelete(card, index, actionType)}
                            onDuplicate={() => onDuplicate && onDuplicate(card, actionType)}
                            onEdit={() => {
                                setEditIndex(index);
                                setEditValue(card);
                                setIsEdit(true);
                                handleAddCard(actionType);
                            }}
                            {...props}
                        />
                    ))}
                {/* Add New Card Button */}
                <IconButton
                    onClick={() => handleAddCard(actionType)}
                    sx={{
                        width: '100%',
                        height: 100,
                        backgroundColor: theme[themeStyle].deep_grey,
                        borderRadius: '10px',
                        '&:hover': { backgroundColor: theme[themeStyle].deep_grey },
                    }}>
                    <AddIcon
                        fontSize="large"
                        sx={{
                            color: theme[themeStyle].highlight,
                        }}
                    />
                </IconButton>

                {/* Form Generator */}
                <FormGenerator
                    formFields={specificSystemForms?.form!}
                    open={!!openForms[actionType]} // Ensure only the clicked form is open
                    onClose={() => handleCloseForm(actionType)}
                    onSubmit={handleOnSubmit}
                    initialValue={initialValue}
                    header={specificSystemForms?.title}
                    initialEditValue={editValue}
                    isEdit={isEdit}
                    initialFormData={initialFormData}
                />
            </Stack>
        );
    }

    return (
        <Box
            sx={{
                width: '100%',
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 2,
            }}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    gap: 2,
                    justifyContent: 'flex-start', // Center the cards
                }}>
                {/* Render Ticket Cards */}
                {!!actionValues &&
                    Array.isArray(actionValues) &&
                    actionValues.map((card, index) => (
                        <TicketCard
                            ticket={card}
                            key={index}
                            onDelete={() => onDelete && onDelete(card, index, actionType)}
                            onDuplicate={() => onDuplicate && onDuplicate(card, actionType)}
                            onEdit={() => {
                                setEditIndex(index);
                                setEditValue(card);
                                setIsEdit(true);
                                handleAddCard(actionType);
                            }}
                        />
                    ))}

                {/* Add New Card Button */}
                <IconButton
                    onClick={() => handleAddCard(actionType)}
                    sx={{
                        width: 60,
                        height: 60,
                        backgroundColor: theme[themeStyle].deep_grey,
                        borderRadius: '2px',
                        '&:hover': {
                            backgroundColor: theme[themeStyle].deep_grey_lighter,
                        },
                    }}>
                    <AddIcon
                        fontSize="large"
                        sx={{
                            color: theme[themeStyle].highlight,
                        }}
                    />
                </IconButton>
            </Box>

            {/* Form Generator */}
            <FormGenerator
                formFields={specificSystemForms?.form!}
                open={!!openForms[actionType]} // Ensure only the clicked form is open
                onClose={() => handleCloseForm(actionType)}
                onSubmit={handleOnSubmit}
                initialValue={initialValue}
                header={specificSystemForms?.title}
                initialEditValue={editValue}
                isEdit={isEdit}
                initialFormData={initialFormData}
            />
        </Box>
    );
};

export default ActionRenderer;
